import {
  Action,
  Card,
  DataStatus,
  EmptyStatus,
  openDialog,
  showNotification,
} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {defaultTo, isNil} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {InspectionType, useCreateInspectionMutation} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
  usePartialUpdateVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetOccupiedSpaceQuery} from '@dms/api/warehouse';
import {
  GetServiceCheckInApiResponse,
  PostServiceCheckInApiArg,
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInDefaultDriverApiQuery,
  useGetServiceCheckInQuery,
  usePatchServiceCheckInMutation,
  usePostServiceCheckInMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getCustomerName, handleApiError, useUpdateOdometer} from '@dms/shared';
import {SendAfterSalesEmailForm, SendAfterSalesCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, getApiDateString, parseDate, useBoolean} from 'shared/web';

import {CheckInHandOverForm} from '../../../../../components/CheckInHandOverForm/CheckInHandOverForm';
import {CheckInHandOverParameter} from '../../../../../components/CheckInHandOverParameter/CheckInHandOverParameter';
import {EmailSmsNotificationBoundary} from '../../../../../components/EmailSmsNotifications/EmailSmsNotificationBoundary';
import {NotificationCategory} from '../../../../../components/EmailSmsNotifications/types/EmailSmsNotificationForm';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {MileageValidationDialog} from '../../../components/MileageValidationDialog';
import {Inspection} from './Inspection';

interface CheckInProps {
  vehicleId: string;
  isReadOnly?: boolean;
}

export function CheckIn(props: CheckInProps) {
  const {serviceCaseId: id} = useWorkshopUrl();

  const {
    data: checkIn,
    isLoading: isCheckInLoading,
    isError: isCheckInError,
  } = useGetServiceCheckInQuery({serviceCaseId: id});

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: iseServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: id});
  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: defaultTo('', serviceCase?.vehicleId)},
    {skip: !serviceCase?.vehicleId}
  );

  const {data: parkingPlace} = useGetOccupiedSpaceQuery(
    {recordId: serviceCase?.vehicleId ?? '', spaceType: 'VEHICLE'},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const {data: vehicleCustomers, isLoading: isVehicleCustomersLoading} = useGetVehicleCustomerQuery(
    {vehicleId: vehicle?.id},
    {skip: isNil(vehicle)}
  );

  const vehicleOperatorCustomerId = vehicleCustomers?.find(
    (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
  )?.customerId;

  const {data: vehicleOperatorCustomer, isLoading: isVehicleOperatorCustomerLoading} =
    useGetCustomerV2Query(
      {
        customerId: vehicleOperatorCustomerId ?? '',
      },
      {
        skip: !vehicleOperatorCustomerId,
      }
    );

  const [handleUpdateOdometer] = useUpdateOdometer(serviceCase?.vehicleId);
  const [postServiceCheckIn] = usePostServiceCheckInMutation();
  const [patchServiceCheckIn, patchServiceCheckInQuery] = usePatchServiceCheckInMutation();
  const [partialUpdateVehicle] = usePartialUpdateVehicleMutation();
  const {data: vehicleMilage, isLoading: isVehicleMileageLoading} = useGetVehicleMileageQuery({
    vehicleId: serviceCase?.vehicleId ?? '',
  });
  const [createInspection, {isLoading: isCreatingInspection, isError: isInspectionError}] =
    useCreateInspectionMutation();

  const [isEditing, setEditing, cancelEditing] = useBoolean();

  const {
    data: checkInDefaultDriver,
    isLoading: isCheckInDefaultDriverLoading,
    isError: isCheckInDefaultDriverError,
  } = useGetServiceCheckInDefaultDriverApiQuery({serviceCaseId: id});

  const inspectionId = checkIn?.inspectionId?.[0];

  const handleSaveCheckIn = async (data: PostServiceCheckInApiArg['body']) => {
    if (isNotNil(data) && inspectionId) {
      data.inspectionId = [inspectionId];
    }

    if (data?.technicalInspection && vehicle) {
      partialUpdateVehicle({
        vehicleId: vehicle.id,
        patchVehicleRequestBody: {
          state: {
            technicalInspectionValidUntil: getApiDateString(parseDate(data?.technicalInspection)),
          },
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    const recordTitle = `${vehicle?.title} - ${getCustomerName(vehicleOperatorCustomer)} (${i18n.t('entity.workshop.labels.checkIn')})`;

    if (checkIn) {
      await patchServiceCheckIn({body: data, serviceCaseId: id})
        .unwrap()
        .then((response) => {
          if (isFalse(response?.isMileageValid)) {
            openDialog(
              <MileageValidationDialog
                highestMileage={response?.highestMileage}
                data-testid={testIds.workshop.serviceCaseDetail('mileageValidation')}
              />,
              {
                size: 'small',
              }
            );
          }

          if (serviceCase && (vehicleMilage?.mileage ?? 0) < (data?.mileage ?? 0)) {
            handleUpdateOdometer({
              recordId: serviceCase.id ?? '',
              milage: data?.mileage ?? 0,
              action: 'service',
              recordTitle,
            });
          }

          cancelEditing();
        })
        .catch(handleApiError);
      return;
    }

    await postServiceCheckIn({body: data, serviceCaseId: id})
      .unwrap()
      .then((response) => {
        if (isFalse(response?.isMileageValid)) {
          openDialog(
            <MileageValidationDialog
              highestMileage={response?.highestMileage}
              data-testid={testIds.workshop.serviceCaseDetail('mileageValidation')}
            />,
            {
              size: 'small',
            }
          );
        }

        cancelEditing();

        handleUpdateOdometer({
          recordId: id,
          milage: data?.mileage ?? 0,
          action: 'service',
          recordTitle,
        });
      })
      .catch(handleApiError);
  };

  const vehicleDefaultValues: GetServiceCheckInApiResponse = {
    technicalInspection: vehicle?.state?.technicalInspectionValidUntil,
  };

  const emptyDefaultValues: GetServiceCheckInApiResponse = {
    driver: {
      name: `${checkInDefaultDriver?.driverFirstname ?? ''} ${checkInDefaultDriver?.driverLastname ?? ''}`.trim(),
      email: checkInDefaultDriver?.driverEmail || null,
      phoneNumber: checkInDefaultDriver?.driverPhoneNo
        ? {
            countryCode: checkInDefaultDriver?.driverPhoneCountryCode || '',
            number: checkInDefaultDriver?.driverPhoneNo || '',
            prefix: checkInDefaultDriver?.driverPhonePrefix || '',
          }
        : null,
      identityCard: checkInDefaultDriver?.driverIdentityCard || null,
    },
    consentToTestDrive: true,
    ...vehicleDefaultValues,
  };

  const isLoading =
    isCheckInLoading ||
    isServiceCaseLoading ||
    isVehicleLoading ||
    isCheckInDefaultDriverLoading ||
    isVehicleMileageLoading ||
    isVehicleCustomersLoading ||
    isVehicleOperatorCustomerLoading;
  const isError =
    isCheckInError || iseServiceCaseError || isVehicleError || isCheckInDefaultDriverError;
  const isFormView = !checkIn || checkIn?.state === 'SCHEDULED' || isEditing;

  const cardActions = buildArray<Action>().whenNot(isFormView || props.isReadOnly, {
    type: 'button',
    variant: 'link',
    leftIcon: 'image/edit',
    title: i18n.t('general.actions.edit'),
    onClick: setEditing,
    'data-testid': testIds.workshop.serviceCaseDetail('checkInEdit'),
  });

  const parkingPlaceName =
    `${parkingPlace?.prefix ?? ''}${parkingPlace?.number ?? ''}` || undefined;

  return (
    <VStack spacing={4}>
      <Card title={i18n.t('general.labels.basicInformation')} actions={cardActions}>
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          data-testid={testIds.workshop.serviceCaseDetail('checkInStatus')}
        >
          <Show when={isFormView}>
            <CheckInHandOverForm
              data-testid={testIds.workshop.serviceCaseDetail('checkIneditForm')}
              defaultValues={checkIn ?? emptyDefaultValues}
              onSubmit={handleSaveCheckIn}
              isCheckin
              isEditing={isEditing}
              isReadOnly={props.isReadOnly}
              onEditDiscard={cancelEditing}
            />
          </Show>
          <Hide when={isFormView}>
            <CheckInHandOverParameter
              data={checkIn}
              parkingPlace={parkingPlaceName}
              data-testid={testIds.workshop.serviceCaseDetail('checkInlist')}
              isCheckin
            />
          </Hide>
        </DataStatus>
      </Card>
      <Show when={isNil(inspectionId)}>
        <Card
          title={i18n.t('page.Inspection.labels.title')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              leftIcon: 'content/add_circle',
              title: i18n.t('general.labels.createNew'),
              isDisabled: isNil(checkIn) || props.isReadOnly,
              isLoading: isCreatingInspection || patchServiceCheckInQuery.isLoading,
              onClick: () => {
                createInspection({
                  vehicleId: props.vehicleId,
                  createAuditRequestBody: {
                    inspectionType: InspectionType.CHECKIN,
                  },
                })
                  .unwrap()
                  .then(({id}) =>
                    patchServiceCheckIn({
                      body: {
                        ...checkIn,
                        inspectionId: [id],
                      },
                      serviceCaseId: serviceCase?.id || '',
                    }).unwrap()
                  )
                  .catch(handleApiError);
              },
            },
          ]}
        >
          <EmptyStatus headline={i18n.t('entity.order.actions.checkInEmpty')} />
        </Card>
      </Show>
      <Show when={!!checkIn && !!inspectionId}>
        <Inspection
          inspectionId={inspectionId || ''}
          vehicleId={props.vehicleId}
          isReadOnly={props.isReadOnly}
          isCompletionDisabled={isNil(checkIn?.inspectionId?.[0])}
          isError={isInspectionError}
          data-testid={testIds.workshop.serviceCaseDetail('checkinInspection')}
          onInspectionDeleted={() => {
            if (!checkIn) {
              return;
            }

            const {inspectionId: _, ...rest} = checkIn;

            patchServiceCheckIn({serviceCaseId: id, body: rest}).unwrap().catch(handleApiError);
          }}
          onSendEmailNotification={() => {
            openDialog(
              <SendAfterSalesEmailForm
                serviceCaseId={id}
                defaultValues={{defaultCategory: 'service-customer_check_in'}}
              />,
              {
                id: 'email-sms-notification-dialog',
                size: 'large',
                title: i18n.t('general.actions.sendEmail'),
              }
            );
          }}
          onSendSmsNotification={() => {
            if (isNil(serviceCase?.customerId)) {
              showNotification.error(i18n.t('general.notifications.notFound'));
              return;
            }

            openDialog(
              <SendAfterSalesCustomerSmsForm
                serviceCaseId={id}
                resourceId={EntityResourceIds.serviceCase}
                recordId={id}
              />,
              {
                title: i18n.t('general.actions.sendSms'),
                size: 'large',
                scrollBehavior: 'outside',
              }
            );
          }}
          onSendEmailSmsNotification={() => {
            openDialog(
              <EmailSmsNotificationBoundary
                serviceCaseId={id}
                dialogId="email-sms-notification-dialog-checkin"
                data-testid={testIds.workshop.serviceCaseDetail('emailSmsNotificationHandover')}
                defaultCategory={NotificationCategory.CHECKIN}
              />,
              {
                id: 'email-sms-notification-dialog-checkin',
                size: 'large',
                title: i18n.t('entity.order.actions.sendEmailSms'),
              }
            );
          }}
        />
      </Show>
    </VStack>
  );
}
